import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

interface EmptyStateProps {
  /** O que está vazio, em uma frase. */
  title: string;
  /** O que a pessoa pode fazer a respeito. Lista vazia sem saída é beco. */
  description?: string;
  /** Um ícone acima do título. No `hero`, ele ganha o disco atrás. */
  icon?: ReactNode;
  /**
   * `hero` é para o vazio que é uma BOA notícia — hoje, só a fila de análise
   * zerada (decisão 31). Ele perde a borda tracejada de propósito: a moldura
   * tracejada é a linguagem de *"faltou conteúdo aqui"*, e ali não faltou nada
   * — está feito.
   */
  variant?: 'default' | 'hero';
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon,
  variant = 'default',
  action,
  className,
}: EmptyStateProps) {
  const isHero = variant === 'hero';

  return (
    <div
      className={cn(
        'flex animate-rise-in flex-col items-center justify-center px-6 text-center',
        isHero
          ? // Respiro vertical no lugar da moldura. Em 375px ele encolhe: nada
            // de tela cheia de vazio no celular, que empurraria o histórico
            // logo abaixo para fora da primeira dobra sozinho.
            'py-16 sm:py-20'
          : 'rounded-lg border border-dashed border-border py-12',
        className,
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className={cn(
            'mb-3 flex items-center justify-center text-primary',
            isHero ? 'size-12 rounded-full bg-primary/10' : 'text-muted-foreground',
          )}
        >
          {icon}
        </span>
      ) : null}

      <p className={cn(isHero ? 'text-lg font-semibold tracking-tight' : 'text-sm font-medium')}>
        {title}
      </p>
      {description ? (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
